import { helpdeskLabel } from '@/constants/helpdesk-labels';
import { isPrimaryUnit, isSharedInbox, otherUnitNames } from '../inbox-units';
import type { BusinessUnit, Inbox } from '@/types';

/** 'Channel::Api' -> 'Api'. Candy stores the Rails class name; the prefix is noise here. */
const shortChannel = (channelType: string) => channelType.replace(/^Channel::/, '');

/**
 * One fact about an inbox. Flat and square on purpose: the row's actions are outlined pills,
 * and a bordered pill beside them reads as one more button.
 */
function FactTag({ children }: { children: React.ReactNode }) {
  return (
    <span className='shrink-0 rounded-sm bg-muted px-1.5 py-0.5 text-xs text-muted-foreground'>
      {children}
    </span>
  );
}

interface Props {
  inbox: Inbox;
  /** The unit whose inbox list this row is in — "Primary" and "Shared with" are relative to it. */
  businessUnit: BusinessUnit;
  allUnits: readonly BusinessUnit[];
}

/**
 * What an inbox row says about the inbox, on its own line under the name: the destination it
 * answers for, then its tags. Nothing here is clickable — the actions sit on the line above.
 */
export function InboxFacts({ inbox, businessUnit, allUnits }: Props) {
  const destination = inbox.destination;
  const shared = isSharedInbox(inbox);

  return (
    <div className='flex flex-wrap items-center gap-1.5'>
      {destination ? (
        // The label is what a human recognises — a support address, a brand. The identifier
        // is an opaque provider-side id, so it follows the label rather than leading.
        <span className='min-w-0 truncate font-mono text-xs text-muted-foreground'>
          {destination.label
            ? `${destination.label} · ${destination.identifier}`
            : destination.identifier}
        </span>
      ) : (
        <span className='text-xs text-muted-foreground'>
          No destination — nothing routes here yet.
        </span>
      )}
      {destination && <FactTag>{helpdeskLabel(destination.provider)}</FactTag>}
      {destination?.channel_type && <FactTag>{destination.channel_type}</FactTag>}
      <FactTag>{shortChannel(inbox.channel_type)}</FactTag>
      {shared && isPrimaryUnit(inbox, businessUnit.id) && <FactTag>Primary</FactTag>}
      {shared && (
        <FactTag>
          Shared with {otherUnitNames(inbox, businessUnit.id, allUnits).join(', ')}
        </FactTag>
      )}
    </div>
  );
}
